<script setup lang="ts">
//#region imports
import numeral from 'numeral';
import hash from 'object-hash';
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import { usePopupManager } from '@/components/PopupManager.vue';
import { saveBlob } from '@/utils/fileSaver';
import { useSegmentRules } from '@/views/CRM/composables/useSegmentRules';
import { useLodash } from '@/setup/lodash';
import ActivityCheckControl from '@/views/CRM/Activities/components/ActivityCheckControl.vue';
import CrmTag from '@/views/CRM/components/CrmTag.vue';
import CrmErrorPanel from '@/views/CRM/components/CrmErrorPanel.vue';
import AddButton from '@/components/AddButton.vue';
import type {
  CrmSegment,
  CrmActivity,
  CrmSegmentListItem,
  CrmSegmentStats,
  CrmSegmentByIdStatsFilters,
  CrmSegmentRuleFilters,
} from '@/types/crm';
//#endregion

//#region types
type CrmActivityExtended = CrmActivity & {
  crm_segment?: CrmSegment | null;
};

interface Props {
  canReview?: boolean;
  isCreate?: boolean;
  viewSegment?: boolean;
  loading?: boolean;
  state?: string;
  activity?: CrmActivityExtended;
  oldActivity?: CrmActivityExtended;
  segments?: CrmSegmentListItem[];
  currentBrand?: string;
  reworkState?: CrmActivity;
  errors?: Record<string, any>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  canReview: false,
  isCreate: false,
  viewSegment: false,
  loading: false,
  state: 'edit',
  activity: () => ({} as CrmActivityExtended),
  oldActivity: () => ({} as CrmActivityExtended),
  segments: () => [],
  currentBrand: '',
  reworkState: () => ({} as CrmActivity),
  errors: () => ({}),
});
//#endregion

//#region composables
const api = useApi();
const popupManager = usePopupManager();
const store = useStore();
const _ = useLodash();
const { t } = useI18n();
const { clearRules, clearSegment } = useSegmentRules();
//#endregion

//#region state
const showFiltersMode = ref<'none' | 'preview' | 'edit'>('preview');
const popupType = ref<'new' | 'edit'>('new');
const localSegment = ref<CrmSegment | null>(null);
const localSegmentId = ref('');
const globalSegmentId = ref('');
const segmentStats = ref<CrmSegmentStats>(getDefaultSegmentStats());
const segmentType = ref<'global' | 'local'>('global');
const autocompleteQuery = ref('');
const autocompleteQueryResult = ref<CrmSegmentListItem[] | null>(null);
const allSegmentEvnSearch = ref<CrmSegmentListItem[]>([]);
const segmentPermission = ref<Record<string, any>>({});
const statsLoading = ref(false);
//#endregion

//#region computed
const segmentList = computed(() => ([
  {
    value: 'local',
    name: t('crm.activities.segment.local'),
    disabled: props.loading,
  },
  {
    value: 'global',
    name: t('crm.activities.segment.global'),
    disabled: props.loading || (!getCanViewSegments() && globalSegmentId.value === ''),
  },
]));

const canEditSegment = computed(() => segmentPermission.value.can_edit);

const isFullPauseEdit = computed(() =>
  props.state === 'pause-edit' && props.activity?.crm_segment_type === 'dynamic',
);

const isSegmentEditMode = computed(() =>
  ['edit', 'rework-edit'].includes(props.state) || isFullPauseEdit.value,
);

const isDisplayPlayers = computed(() => {
  if (statsLoading.value) {
    return false;
  }

  if (props.activity.id) {
    return props.activity.is_players_attached;
  }

  return true;
});

const isSuperUser = computed(() => Boolean(store.getters['auth/adminAcl']?.is_superuser));

const localSegmentErrors = computed(() => {
  const hasErrors = getErrors('crm_segment').length > 0;
  return hasErrors && !Array.isArray(props.errors.crm_segment)
    ? props.errors.crm_segment
    : {};
});

const filtersSegmentError = computed(() => {
  if (!props.errors.filters) return [];

  const filtersError = getError('filters', '');

  if (Array.isArray(filtersError)) {
    return filtersError.map(({ message }) => message);
  }

  return [getError('filters')];
});

const errorsDetailed = computed(() => {
  return [
    getError('crm_segmen_id'),
    ...getLocalSegmentError('crm_segment'),
    ...filtersSegmentError.value,
  ].filter((item) => !!item);
});

const hasError = computed(() => errorsDetailed.value.length > 0);

const globalSegment = computed<CrmSegment | null>(() => {
  return (clearDublicate('id', [...props.segments, ...allSegmentEvnSearch.value]) as CrmSegmentListItem[])
    .find((segmentItem: CrmSegmentListItem) => segmentItem.id === globalSegmentId.value) || null;
});

const segment = computed({
  get(): CrmSegment | null {
    return (segmentType.value === 'global' ? globalSegment.value : localSegment.value);
  },
  set(value: CrmSegment | null) {
    if (segmentType.value === 'global') {
      globalSegmentId.value = value ? value.id : '';
    } else {
      localSegment.value = value;
    }
  },
});

const hasFilters = computed(() => props.activity.filters && props.activity.filters.length);

function sortSegmentsByRegion(source: CrmSegmentListItem[]) {
  return [...source].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ru', { sensitivity: 'base' }));
}

const approvedSegments = computed(() => {
  const source = autocompleteQueryResult.value ? autocompleteQueryResult.value : props.segments;
  return sortSegmentsByRegion(source.filter((segmentItem) => segmentItem.moderation_status === 'approved'));
});

const segmentId = computed(() => props.activity.crm_segment_id);

const isSegmentChanged = computed(() => {
  return props.activity.crm_segment_id !== props.oldActivity.crm_segment_id
    || props.activity.crm_segment !== props.oldActivity.crm_segment;
});

const isFilterChanged = computed(() => {
  const activityFiltersHash = hash(props.activity.filters, { algorithm: 'md5' });
  const oldActivityFiltersHash = hash(props.oldActivity.filters, { algorithm: 'md5' });
  return activityFiltersHash !== oldActivityFiltersHash;
});

const playersCount = computed(() => {
  if (!isSegmentChanged.value && !isFilterChanged.value) {
    const players = props.activity.players_count;
    return typeof players === 'number' ? numeral(players).format('0,0') : players;
  }
  return segmentStats.value.players_count;
});

const isApprovedSegmentModel = computed({
  get: () => !!props.reworkState.is_approved_segment,
  set: (value) => {
    props.reworkState.is_approved_segment = value;
  },
});

const isHiddenStats = computed(() => {
  return segment.value?.type === 'manual'
    && segmentType.value === 'local'
    && isSegmentEditMode.value;
});

const typeIcon = computed(() => {
  switch (segment.value?.type) {
    case 'dynamic':
      return 'wave-square';
    case 'manual':
      return 'gear';
    default:
      return 'lock';
  }
});
//#endregion

//#region methods
function clearDublicate(key: string, arr: any[]) {
  return _.values(_.keyBy(arr, key));
}

async function exportData() {
  const file = await api.getActivityPlayers(props.activity.id);
  const linkBlob = new Blob([file.data], { type: file.headers['content-type'] });
  saveBlob(linkBlob, segment.value?.name || '');
}

function getSegments(search?: string, action?: string) {
  return api
    .getSegments({
      site_id: props.currentBrand,
      schema: 'short_list',
      moderation_status: 'approved',
      search,
    } as any)
    .then((response) => {
      if (segment.value?.id) {
        segmentPermission.value = response.data.misc?.permissions?.[segment.value.id] || {};
      }

      if (action === 'ignore') {
        autocompleteQueryResult.value = clearDublicate(
          'id',
          [...(autocompleteQueryResult.value || []), ..._.cloneDeep(response.data.payload)],
        ) as CrmSegmentListItem[];
      } else {
        autocompleteQueryResult.value = sortSegmentsByRegion(_.cloneDeep(response.data.payload));
      }

      allSegmentEvnSearch.value = clearDublicate(
        'id',
        allSegmentEvnSearch.value.concat(autocompleteQueryResult.value || []),
      ) as CrmSegmentListItem[];
    });
}

function handlerVisibleChange(isVisible: boolean) {
  if (!isVisible) {
    setTimeout(() => {
      autocompleteQueryResult.value = allSegmentEvnSearch.value;
    }, 200);
  }
}

function setAutocompleteQuery(value: string) {
  autocompleteQuery.value = value;
}

function getLocalSegmentError(field: string) {
  if (!Array.isArray(props.errors[field])) {
    const filtersError = getError(field, ['rules_group']);
    if (Array.isArray(filtersError)) {
      return filtersError.map(({ message }) => message);
    }

    return getErrors(field, t('crm.activities.segment.local_segment_error'));
  }

  return [getError(field)];
}

function getError(field: string, path: any = [0, 'message']) {
  return _.get(props.errors, [field, ...path]);
}

function getErrors(field: string, message = '') {
  if (props.errors[field]) {
    if (Array.isArray(props.errors[field])) {
      return props.errors[field].length > 0 ? [message] : [];
    }

    const keys = Object.keys(props.errors[field]);
    return keys.length > 0 ? [message] : [];
  }

  return [];
}

function getLocalSegment(value = localSegmentId.value) {
  if (value) {
    api.getSegment(value).then((response) => {
      localSegment.value = response.data.payload;
    });
  }
}

function updateSegmentStats() {
  const isEdit = isSegmentEditMode.value;

  if (segmentId.value && (segment.value?.is_global || !isEdit)) {
    let filters = props.activity.crm_segment_is_global ? props.activity.filters : [];
    filters = clearRules(filters);

    const typedFilters = filters as CrmSegmentByIdStatsFilters;

    return api.getSegmentStatsById(segmentId.value, typedFilters).then((response) => {
      segmentStats.value = response.data.payload;
    });
  }

  if (segment.value && !(segment.value.type === 'manual' && isEdit)) {
    if (!_.isEmpty(segment.value)) {
      const checkSegment = clearSegment(segment.value);
      if (segment.value.type !== 'manual' && checkSegment.rules_group.rules.length === 0) {
        segmentStats.value = getDefaultSegmentStats();
      } else {
        statsLoading.value = true;

        return api
          .getSegmentStats({
            ...checkSegment,
            site_id: props.currentBrand,
          } as any)
          .then((response) => {
            segmentStats.value = response.data.payload;
          })
          .finally(() => {
            statsLoading.value = false;
          });
      }
    }
  }

  return Promise.resolve(getDefaultSegmentStats());
}

function setActivityField(field: keyof CrmActivityExtended, value: any) {
  (props.activity as any)[field] = value;
}


// used at Activity.vue
function setSegmentId(value: string, restore = false, stats: CrmSegmentStats | null = null) {
  segmentType.value = props.activity.crm_segment_is_global ? 'global' : 'local';

  if (props.activity.crm_segment_is_global) {
    globalSegmentId.value = value;
    localSegmentId.value = '';
  } else {
    const isForceRestoreLocal = restore && localSegmentId.value === value;
    localSegmentId.value = value;
    globalSegmentId.value = '';

    if (isForceRestoreLocal) {
      getLocalSegment(localSegmentId.value);
    }
  }

  if (stats) {
    segmentStats.value = _.cloneDeep(stats);
  } else {
    updateSegmentStats();
  }
}

function handleAddLocalSegment() {
  openCreateSegmentPopup('new');
}

function handleEditLocalSegment() {
  openCreateSegmentPopup('edit');
}

function handlePreviewSegment() {
  showFiltersMode.value = segmentType.value === 'local' ? 'none' : 'preview';
  openViewSegmentPopup();
}

function handleAddFilter() {
  showFiltersMode.value = 'edit';
  openViewSegmentPopup();
}

function handleDeleteSegment() {
  segment.value = null;
  globalSegmentId.value = '';
  setActivityField('crm_segment', {});
  setActivityField('crm_segment_id', '');
}

function openCreateSegmentPopup(type: 'new' | 'edit') {
  popupType.value = type;
  popupManager.createSegment.open({
    data: {
      segmentId: segmentId.value,
      currentBrand: props.currentBrand,
      isLocalSegment: segmentType.value === 'local',
      localSegment: localSegment.value,
      localSegmentErrors: localSegmentErrors.value,
    },
    listeners: {
      updateLocalSegment: handleUpdateLocalSegment,
    },
  });
}

function openViewSegmentPopup() {
  popupManager.viewSegmentPopup.open({
    data: {
      activityFilters: props.activity.filters,
      segmentId: segmentId.value,
      currentBrand: props.currentBrand,
      showFiltersMode: showFiltersMode.value,
      showEdit: false,
      playerAttached: props.activity.is_players_attached,
      activity: props.activity,
      isLocalSegment: segmentType.value === 'local',
      activitySegmentType: segmentType.value,
      state: props.state,
      localSegment: localSegment.value,
    },
    listeners: {
      'update:activity-filters': (filters: CrmSegmentRuleFilters) => {
        setActivityField('filters', filters);
        updateSegmentStats();
      },
      updateFilterStats: handleUpdateFilterStats,
    },
  });
}

function handleUpdateLocalSegment(nextSegment: CrmSegment) {
  localSegment.value = _.cloneDeep(nextSegment);
  segmentStats.value = getDefaultSegmentStats();
  updateSegmentStats();
  segment.value = localSegment.value;
  setActivityField('crm_segment', segment.value);
  setActivityField('crm_segment_is_global', false);
  globalSegmentId.value = '';
}

function handleUpdateFilterStats(nextSegmentStats: CrmSegmentStats) {
  segmentStats.value = _.cloneDeep(nextSegmentStats);
}

function handleUpdateGlobalSegment(item: string) {
  globalSegmentId.value = item;
  segment.value = globalSegment.value;
  setActivityField('crm_segment_id', item);
  setActivityField('crm_segment_is_global', true);
  localSegment.value = null;
  updateSegmentStats();
}

function getDefaultSegmentStats(): CrmSegmentStats {
  return {
    players_count: '0',
    deposits_all_avg: '0',
    deposits_all_median: '0',
    avg_sports_bet_amount: '0',
    median_sports_bet_amount: '0',
    avg_games_bet_amount: '0',
    median_games_bet_amount: '0',
  };
}

function getCanViewSegments() {
  const isUser = !isSuperUser.value && store.getters['auth/currentAcl'].crm_segments_view !== 'deny';
  return isSuperUser.value || isUser;
}
//#endregion

//#region watch
watch(() => props.activity.crm_segment_id, async (nv) => {
  if (nv) {
    await getSegments(props.activity.crm_segment_name, 'ignore');
  }
});

watch(localSegmentId, (value) => {
  if (value && !localSegment.value) {
    getLocalSegment(value);
  }
});

watch(autocompleteQuery, (value) => {
  if (!value) {
    autocompleteQueryResult.value = null;
  } else {
    getSegments(value);
  }
});
//#endregion

//#region hooks
onMounted(() => {
  getSegments(globalSegmentId.value);
});
//#endregion

defineExpose({
  getDefaultSegmentStats,
  getLocalSegment,
  setSegmentId,
  segmentType,
  segment,
});
</script>

<template>
  <FormRow
    class="crm-activity-segment"
    :error="hasError"
  >
    <div class="t:space-y-16">
      <div
        class="t:flex t:items-center t:gap-8"
      >
        <UiLabel
          :label="$t('crm.activities.segment.title')"
          :font-size="20"
          bold
        />
        <template v-if="isSegmentEditMode">
          <UiSeparator
            type="dot"
            color="--background-primary"
          />
          <div
            class="crm-activity-segment__hint"
            v-html="$t('crm.activities.segment.hint')"
          />
        </template>
      </div>
      <div
        v-if="isSegmentEditMode"
        class="t:flex t:items-end t:gap-12"
      >
        <UiTabs
          v-model="segmentType"
          :label="$t('crm.segments.type.title')"
          :list="segmentList"
          size="big"
        />
        <AddButton
          v-if="segmentType === 'local'"
          :is-disabled="!!segment"
          @click="handleAddLocalSegment"
        >
          {{ $t('crm.activities.segment.create_button') }}
        </AddButton>
        <UiSelectElement
          v-if="segmentType === 'global'"
          v-model="globalSegmentId"
          :placeholder="$t('ui.select.default')"
          :list="approvedSegments"
          filterable
          fit-dropdown-width
          :remote="props.segments.length > 0"
          :remote-method="setAutocompleteQuery"
          size="big"
          @change="handleUpdateGlobalSegment"
          @visible-change="handlerVisibleChange"
        />
      </div>
      <Panel v-if="segment">
        <template v-slot:prepend>
          <ActivityCheckControl
            v-if="state === 'rework' || (state === 'check' && canReview)"
            v-model="isApprovedSegmentModel"
            :can-edit="state === 'check'"
          />
        </template>
        <div class="t:flex t:items-center t:justify-between t:w-full">
          <div class="t:flex t:items-center t:gap-8">
            <div class="crm-activity-segment__name">
              {{ segment.name }}
            </div>
            <UiSeparator type="dot" />
            <CrmTag
              preset="contrast"
              :icon="typeIcon"
            >
              {{ $t(`crm.segments.type.${segment.type}`) }}
            </CrmTag>
            <UiSeparator type="dot" />
            <CrmTag
              preset="contrast"
              :icon="props.activity.crm_segment_is_global ? 'globe' : 'sliders-h'"
            >
              {{ props.activity.crm_segment_is_global ? $t('crm.activities.segment.global') : $t('crm.activities.segment.local') }}
            </CrmTag>
            <template
              v-if="!isHiddenStats"
            >
              <UiSeparator type="dot" />
              <CrmTag
                preset="contrast"
                icon="users"
              >
                <div
                  v-loading="!isDisplayPlayers"
                  class="crm-activity-segment__loading"
                >
                  <span v-if="isDisplayPlayers">{{ playersCount }}</span>
                </div>
              </CrmTag>
            </template>
            <template
              v-if="segmentType === 'global' && segment && segment.moderation_status !== 'approved'"
            >
              <UiSeparator type="dot" />
              <CrmTag
                preset="red"
                icon="thumbs-down"
              >
                {{ $t('crm.activities.segment.not_approved') }}
              </CrmTag>
            </template>
            <template
              v-if="hasFilters"
            >
              <UiSeparator type="dot" />
              <CrmTag
                preset="red"
                icon="filter"
              >
                {{ $t('crm.activities.segment.filtered') }}
              </CrmTag>
            </template>
          </div>
          <div class="t:flex t:items-center t:-my-8 t:-mr-8">
            <ActionIcon
              v-if="(viewSegment || canEditSegment) || !props.activity.crm_segment_is_global"
              key="preview"
              :tooltip="$t('crm.tooltips.preview')"
              icon="eye"
              :disabled="!props.activity.crm_segment_id || isHiddenStats || (isCreate && !!localSegment)"
              @click="handlePreviewSegment"
            />
            <ActionIcon
              v-if="(viewSegment || !props.activity.crm_segment_is_global) && state === 'approved'"
              :tooltip="$t('crm.tooltips.download')"
              icon="down"
              @click="exportData"
            />
            <template v-if="isSegmentEditMode">
              <template v-if="!props.activity.crm_segment_is_global">
                <ActionIcon
                  key="edit"
                  :tooltip="$t('crm.tooltips.edit')"
                  icon="pen"
                  @click="handleEditLocalSegment"
                />
                <ActionIcon
                  key="delete"
                  :tooltip="$t('crm.tooltips.delete')"
                  icon="trash-alt"
                  color="var(--text-negative)"
                  @click="handleDeleteSegment"
                />
              </template>
              <ActionIcon
                v-if="(viewSegment || canEditSegment) && props.activity.crm_segment_is_global"
                key="filter"
                :tooltip="$t('crm.tooltips.filter')"
                icon="filter"
                @click="handleAddFilter"
              />
            </template>
          </div>
        </div>
      </Panel>
      <div
        v-if="errorsDetailed.length"
        class="t:flex t:flex-col t:items-start t:space-y-8"
        :class="{
          't:mt-8': segment
        }"
      >
        <CrmErrorPanel
          v-for="(error, index) in errorsDetailed"
          :key="index"
        >
          {{ error }}
        </CrmErrorPanel>
      </div>
    </div>
  </FormRow>
</template>

<style lang="scss">
.crm-activity-segment {
  &.form-row {
    padding: 16px 24px;
  }
  &__loading {
    height: 100%;
    min-width: 16px;
    z-index: 9;
    .el-loading-spinner {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      margin-top: 0;
      transform: translateY(-50%);

      .circular {
        height: 100%;
        width: 10px;
        .path {
          stroke: var(--text-positive);
        }
      }
    }

  }
  &__name {
    color: var(--text-body);
    font-size: 16px;
    font-weight: 400;
  }
  &__hint {
    color: var(--text-body-secondary);
    font-size: 14px;
  }
}
</style>
